// hooks/useGlobalStock.js
import { useState, useCallback } from "react";
import axios from "axios";
import { useMarketToggle } from "../context/MarketContext";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const useGlobalStock = () => {
  const { market } = useMarketToggle();

  // ── daily indicators ──
  const [dailyRSI, setDailyRSI] = useState(null);
  const [dailyMACD, setDailyMACD] = useState(null);

  // ── intraday indicators ──
  const [intradayRSI, setIntradayRSI] = useState(null);
  const [intradayMACD, setIntradayMACD] = useState(null);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [indicatorError, setIndicatorError] = useState(null);

  // ── return data ──
  const [returnData, setReturnData] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState(null);

  // ── moving averages ──
  const [maData, setMaData] = useState(null);
  const [maLoading, setMaLoading] = useState(false);
  const [maError, setMaError] = useState(null);

  // ── historical (pivots) ──
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);

  // ── intraday candles (new) ──
  const [intradayCandle, setIntradayCandle] = useState(null);
  const [candleLoading, setCandleLoading] = useState(false);
  const [candleError, setCandleError] = useState(null);

  // ── news ──
  const [newsData, setNewsData] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);

  //   --- globalsnapshot----
  const [globalSnapshot, setGlobalSnapshot] = useState(null);
  const [globalSnapshotLoading, setGlobalSnapshotLoading] = useState(false);
  const [globalSnapshotError, setGlobalSnapshotError] = useState(null);

  /* ─────────── fetchers ─────────── */

  const fetchGlobalSnapshot = useCallback(async (symbol) => {
    if (!symbol) return null;

    setGlobalSnapshotLoading(true);
    setGlobalSnapshotError(null);

    try {
      const res = await axios.get(`${BASE_URL}/api/globalSnapshot/${symbol}`);

      const snapshot = res?.data?.data ?? null;

      setGlobalSnapshot(snapshot);

      return snapshot;
    } catch (err) {
      console.error(
        "Global snapshot error:",
        err?.response?.data || err.message,
      );

      setGlobalSnapshotError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load global snapshot",
      );

      return null;
    } finally {
      setGlobalSnapshotLoading(false);
    }
  }, []);

  const fetchDailyIndicators = useCallback(async (symbol) => {
    if (!symbol) return;
    setIndicatorError(null);
    try {
      const rsi = await axios.get(
        `${BASE_URL}/api/globalRsi?symbol=${symbol}&type=daily`,
      );
      const macd = await axios.get(
        `${BASE_URL}/api/globalMacd?symbol=${symbol}&type=daily`,
      );
      setDailyRSI(rsi.data);
      setDailyMACD(macd.data);
    } catch (err) {
      setIndicatorError(err.message);
      console.error("Daily indicator failed", err.message);
    }
  }, []);

  const fetchIntradayIndicators = useCallback(async (symbol) => {
    if (!symbol) return;
    setIndicatorError(null);
    try {
      const rsi = await axios.get(
        `${BASE_URL}/api/globalRsi?symbol=${symbol}&type=intraday`,
      );
      const macd = await axios.get(
        `${BASE_URL}/api/globalMacd?symbol=${symbol}&type=intraday`,
      );
      setIntradayRSI(rsi.data);
      setIntradayMACD(macd.data);
    } catch (err) {
      setIndicatorError(err.message);
      console.error("Intraday indicator failed", err.message);
    }
  }, []);

  const fetchAllIndicators = useCallback(
    async (symbol) => {
      setLoadingIndicators(true);
      await Promise.allSettled([
        fetchDailyIndicators(symbol),
        fetchIntradayIndicators(symbol),
      ]);
      setLoadingIndicators(false);
    },
    [fetchDailyIndicators, fetchIntradayIndicators],
  );

  const fetchReturnData = useCallback(async (symbol) => {
    if (!symbol) return;
    setReturnLoading(true);
    setReturnError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/getHistorical/${symbol}`);
      setReturnData(res.data);
    } catch (err) {
      setReturnError(err.message);
      console.log("Return data error:-", err.message);
    } finally {
      setReturnLoading(false);
    }
  }, []);

  const fetchMaData = useCallback(async (symbol) => {
    if (!symbol) return;
    setMaLoading(true);
    setMaError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/globalMa`, {
        params: { symbol },
      });
      setMaData(res.data);
    } catch (err) {
      setMaError(err.message);
      console.log("MA data error:-", err.message);
    } finally {
      setMaLoading(false);
    }
  }, []);

  const fetchHistData = useCallback(async (symbol) => {
    if (!symbol) return;
    setHistLoading(true);
    setHistError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/globalHist`, {
        params: { symbol },
      });
      setHistData(res?.data);
    } catch (err) {
      setHistError(err.message);
      console.log("Hist data error:-", err.message);
    } finally {
      setHistLoading(false);
    }
  }, []);

  const fetchIntradayCandle = useCallback(async (symbol, interval = "1m") => {
    if (!symbol) return;
    setCandleLoading(true);
    setCandleError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/globalIntraday`, {
        params: { symbol, interval },
      });
      setIntradayCandle(res?.data);
    } catch (err) {
      setCandleError(err.message);
      console.log("Intraday candle error:-", err.message);
    } finally {
      setCandleLoading(false);
    }
  }, []);

  const fetchNewsData = useCallback(
    async (symbol) => {
      if (!symbol) return;
      setNewsLoading(true);
      setNewsError(null);
      try {
        const res = await axios.get(`${BASE_URL}/api/stocknews`, {
          params: { ticker: symbol, market },
        });
        setNewsData(res.data);
      } catch (err) {
        setNewsError(err.message);
      } finally {
        setNewsLoading(false);
      }
    },
    [market],
  );

  return {
    // daily indicators
    dailyRSI,
    dailyMACD,
    fetchDailyIndicators,
    // intraday indicators
    intradayRSI,
    intradayMACD,
    fetchIntradayIndicators,
    fetchAllIndicators,
    loadingIndicators,
    indicatorError,
    // returns
    returnData,
    returnLoading,
    returnError,
    fetchReturnData,
    // moving averages
    maData,
    maLoading,
    maError,
    fetchMaData,
    // historical / pivots
    histData,
    histLoading,
    histError,
    fetchHistData,
    // intraday candles
    intradayCandle,
    candleLoading,
    candleError,
    fetchIntradayCandle,
    // news
    newsData,
    newsLoading,
    newsError,
    fetchNewsData,

    globalSnapshot,
    globalSnapshotLoading,
    globalSnapshotError,
    fetchGlobalSnapshot,
  };
};
